<template>
  <Teleport to="body">
    <Transition name="popover">
      <div
        v-if="isDetached"
        class="state-history-popover"
        :style="popoverStyle"
        ref="popoverRef"
      >
        <!-- Header -->
        <div class="popover-header" @mousedown="startDrag">
          <div class="header-left">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span class="title">State History</span>
            <span class="count-badge">{{ stateHistory.length }}</span>
          </div>
          <div class="header-actions">
            <button class="header-btn" @click="attach" title="Reattach to panel">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                <path d="M15 3h6v6" />
                <path d="M10 14 21 3" />
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="popover-content">
          <div v-if="stateHistory.length > 0" class="history-actions">
            <button class="clear-btn" @click="clearHistory">Clear</button>
          </div>

          <div v-if="stateHistory.length === 0" class="history-empty">
            No state changes recorded yet
          </div>

          <div v-else class="history-list">
            <div 
              v-for="entry in [...stateHistory].reverse()" 
              :key="entry.id" 
              class="history-entry"
            >
              <div class="entry-header">
                <span class="entry-source">{{ entry.source }}</span>
                <span class="entry-key">.{{ entry.key }}</span>
                <span class="entry-time">{{ formatHistoryTime(entry.timestamp) }}</span>
              </div>
              <div class="entry-change">
                <span class="old-value">{{ formatValue(entry.oldValue) }}</span>
                <span class="arrow">→</span>
                <span class="new-value">{{ formatValue(entry.newValue) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStateHistory } from "@/composables/useStateHistory";

const {
  stateHistory,
  isDetached,
  popoverPosition,
  attach,
  clearHistory,
  updatePopoverPosition,
  formatHistoryTime,
  formatValue,
} = useStateHistory();

const popoverRef = ref<HTMLElement | null>(null);

// Default dimensions
const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT = 400;

// Popover position style
const popoverStyle = computed(() => {
  const pos = popoverPosition.value;
  
  if (pos.x === -1 || pos.y === -1) {
    // Default position: top-right area
    return {
      right: "20px",
      top: "80px",
      width: `${POPOVER_WIDTH}px`,
      maxHeight: `${POPOVER_HEIGHT}px`,
    };
  }

  return {
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    width: `${POPOVER_WIDTH}px`,
    maxHeight: `${POPOVER_HEIGHT}px`,
  };
});

// Dragging logic
let isDragging = false;
let dragStartX = 0;
let dragStartY = 0;
let popoverStartX = 0;
let popoverStartY = 0;

const startDrag = (e: MouseEvent) => {
  if ((e.target as HTMLElement).closest(".header-btn")) return;

  isDragging = true;
  dragStartX = e.clientX;
  dragStartY = e.clientY;

  const popover = popoverRef.value;
  if (popover) {
    const rect = popover.getBoundingClientRect();
    popoverStartX = rect.left;
    popoverStartY = rect.top;
  }

  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", stopDrag);
  document.body.style.userSelect = "none";
};

const onDrag = (e: MouseEvent) => {
  if (!isDragging) return;

  const deltaX = e.clientX - dragStartX;
  const deltaY = e.clientY - dragStartY;

  updatePopoverPosition(popoverStartX + deltaX, popoverStartY + deltaY);
};

const stopDrag = () => {
  isDragging = false;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.body.style.userSelect = "";
};

onUnmounted(() => {
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
});
</script>

<style scoped>
.state-history-popover {
  position: fixed;
  background: linear-gradient(180deg, #1a1a2e 0%, #16162a 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  display: flex;
  flex-direction: column;
  z-index: 99999998;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  overflow: hidden;
}

/* Header */
.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: move;
  user-select: none;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #60a5fa;
}

.title {
  font-size: 12px;
  font-weight: 600;
  color: #e0e8f0;
  letter-spacing: 0.3px;
}

.count-badge {
  font-size: 10px;
  font-weight: 600;
  color: #8090a0;
  background: rgba(255, 255, 255, 0.08);
  padding: 2px 6px;
  border-radius: 8px;
}

.header-actions {
  display: flex;
  gap: 4px;
}

.header-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #8090a0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  transition: all 0.15s ease;
}

.header-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e8e8f0;
}

/* Content */
.popover-content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.popover-content::-webkit-scrollbar {
  width: 5px;
}

.popover-content::-webkit-scrollbar-track {
  background: transparent;
}

.popover-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

/* History Actions */
.history-actions {
  display: flex;
  justify-content: flex-end;
  padding: 6px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.clear-btn {
  font-size: 10px;
  font-weight: 500;
  color: #808098;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 3px 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.clear-btn:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

/* Empty State */
.history-empty {
  padding: 24px 16px;
  text-align: center;
  font-size: 11px;
  color: #606080;
}

/* History List */
.history-list {
  display: flex;
  flex-direction: column;
}

.history-entry {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.history-entry:last-child {
  border-bottom: none;
}

.entry-header {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
}

.entry-source {
  font-size: 10px;
  font-weight: 600;
  color: #60a5fa;
}

.entry-key {
  font-size: 10px;
  color: #bb9af7;
  font-family: "JetBrains Mono", "Fira Code", monospace;
}

.entry-time {
  font-size: 9px;
  color: #606080;
  margin-left: auto;
  font-family: "JetBrains Mono", "Fira Code", monospace;
}

.entry-change {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
}

.old-value {
  color: #f7768e;
  opacity: 0.8;
}

.arrow {
  color: #606080;
}

.new-value {
  color: #9ece6a;
}

/* Transitions */
.popover-enter-active,
.popover-leave-active {
  transition: all 0.2s ease;
}

.popover-enter-from,
.popover-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
</style>

